import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { NgbActiveModal, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ViewerDataService } from '../../viewer-data.service';
import { ActorActionsApiClient } from 'src/app/services/api-clients/actor-actions.api-client';
import { AdoptionPrefillModel } from '../../models/adoption-prefill.model';
import { ParseCalendarDate } from 'src/app/_helpers/calendarDateHelper';

@Component({
    templateUrl: './perform-action-adopt.modal.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PerformActionAdoptModal implements OnInit {

  @Input() requestName!: string;
  @Output() adoptClicked = new EventEmitter();
  public awaitingAction: boolean = false;
  public adoptfieldIsSet: boolean = false;
  public datefieldIsSet: boolean = false;

  public get disableButton(): boolean {
    return this.awaitingAction || !this.adoptfieldIsSet || !this.datefieldIsSet;
  }
  /**
   * Number of the current actor action
   */
  expectedActionNumber: number = 1;
  currentActorInviteId!: string;

  maxDeadline: Date = new Date(new Date().setDate(new Date().getDate()));
  maxDate!: { year: number; month: number; day: number; };
  startDate!: { year: number; month: number; day: number; };
  vaststellingText: string = "Request.View.Summary.Choose";
  /** Whether a Vaststelling datum is needed */
  isAdopted: boolean = false;
  setDate: string = "";
  date: Date | null = null;
  model: NgbDateStruct | undefined;
  translations = <any>{};
  constructor(public activeModal: NgbActiveModal, private translateService: TranslateService, private viewerDataService: ViewerDataService, private actorActionsApiClient: ActorActionsApiClient) {
    this.viewerDataService.getCurrentActor$().subscribe(currentActor => {
      this.expectedActionNumber = currentActor.badgeNumber;
      this.currentActorInviteId = currentActor.id;
    });
    this.maxDate = { year: this.maxDeadline.getFullYear(), month: this.maxDeadline.getMonth() + 1, day: this.maxDeadline.getDate() };

  }

  ngOnInit(): void {
    this.getTranslations();
    this.prefillFromFilingData();
  }

  /**
   * Starts the fields on the adoption the filing already states, so the actor confirms or corrects it rather
   * than retyping it. Both stay editable.
   * <p>The fields are left empty when nothing is known, which is every request that carries no filing data.</p>
   */
  private prefillFromFilingData(): void {
    if (!this.currentActorInviteId) {
      return; // no actor to ask about, so nothing to prefill from
    }
    this.actorActionsApiClient.getAdoptionPrefill(this.viewerDataService.get_RequestGuid, this.currentActorInviteId)
      .subscribe({
        next: (prefill: AdoptionPrefillModel) => this.applyPrefill(prefill),
        error: (error: any) => {
          // Having nothing to prefill with must not block the action: the actor fills the fields in themselves,
          // exactly as they did before there was a prefill at all.
          console.error("Error fetching the adoption to prefill with:", error);
        }
      });
  }

  private applyPrefill(prefill: AdoptionPrefillModel) {
    if (!prefill) {
      return;
    }
    if (this.adoptfieldIsSet || this.datefieldIsSet) {
      // The actor got there first while this was in flight. Their own answer stands: a prefill is only ever
      // a starting point, and overwriting what someone just typed is worse than not prefilling at all.
      return;
    }
    // The choice first: it decides whether a date is needed at all
    if (prefill.adoptionChoice === true || prefill.adoptionChoice === false) {
      this.changeVastelling(prefill.adoptionChoice ? "Yes" : "No");
    }
    if (prefill.adoptionDate) {
      this.applyPrefilledDate(prefill.adoptionDate);
    }
  }

  private applyPrefilledDate(adoptionDate: string) {
    // An adoption date is a calendar date, so it is read as that day rather than as an instant that the
    // reader's timezone could shift back a day.
    const date = ParseCalendarDate(adoptionDate);
    if (!date) {
      return;
    }
    this.model = { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };
    this.startDate = this.model;
    // through changeDate, so a prefilled date ends up in exactly the shape a picked one posts in
    this.changeDate(this.model);
  }

  getTranslations() {
    this.translateService.get([
      'Request.View.Summary.ChooseDate',
    ]).subscribe(translation => {
      this.translations.ChooseDate = translation['Request.View.Summary.ChooseDate'];
    });
  }
  public changeDate(event: any) {
    document.getElementById('AdoptionDate')?.classList.remove('required');
    document.getElementById('AdoptionDateCalendar')?.classList.remove('required');
    this.setDate = event.year + "-" + event.month + "-" + event.day;
    this.date = new Date(this.setDate);
    this.datefieldIsSet = !!this.date;
  }

  public changeVastelling(event: string) {
    this.vaststellingText = "Request.View.Summary." + event;
    this.isAdopted = (event == "Yes");
    this.adoptfieldIsSet = true;
    if (!this.isAdopted) {
      // A filing that was not adopted has no adoption date. Leaving one behind would file 'Nee' next to a
      // date, which is what the deponeringsgegevens would then state.
      this.clearAdoptionDate();
    }
    this.datefieldIsSet = !!this.date || !this.isAdopted;
  }

  /** Drops the date and what the field shows, so nothing of a previous 'yes' survives the switch to 'no' */
  private clearAdoptionDate() {
    this.date = null;
    this.setDate = "";
    this.model = undefined;
  }

  public getSelectedDate() {
    if (this.date !== null) return this.date.getDate() + "-" + this.date.getMonth() + "-" + this.date.getFullYear();
    return this.translations.ChooseDate;
  }
  back() {
    this.activeModal.close();
  }
  clickAdopt() {
    this.awaitingAction = true;
    let skipAdopton = !this.isAdopted;
    // null rather than an empty string, so a skipped adoption binds to no date at all on the API
    let adoptionDate = this.isAdopted ? this.setDate : null;
    this.actorActionsApiClient.adopt(this.viewerDataService.get_RequestGuid, this.currentActorInviteId, skipAdopton, adoptionDate).subscribe(() => {

      this.viewerDataService.setFieldsOnActor(this.currentActorInviteId,this.isAdopted);
      this.viewerDataService.actorCompletedAction(this.currentActorInviteId);
      this.activeModal.close();
    });
  }

}
